"use client";

import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import YouTubePlayer from "@/components/video/youtube-player";

type VideoContentBlok = SbBlokData & {
  title?: string;
  subtitle?: string;
  youtube_url?: string;
  bg_color?: string;
};

function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function VideoContent({ blok }: { blok: VideoContentBlok }) {
  const id = extractYouTubeId(blok.youtube_url);
  const { bg } = resolveBg(blok.bg_color, "dusk-01");

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[96px]">
        <div className="overflow-hidden rounded-[16px] bg-purple-01 [@media(min-width:940px)]:flex [@media(min-width:940px)]:items-center [@media(min-width:940px)]:gap-[clamp(2rem,5vw,5.25rem)] [@media(min-width:940px)]:rounded-[24px] [@media(min-width:940px)]:pl-[clamp(1.5rem,5vw,4.375rem)]">
          <div className="flex flex-col gap-4 px-5 pt-10 pb-6 [@media(min-width:940px)]:flex-1 [@media(min-width:940px)]:min-w-0 [@media(min-width:940px)]:max-w-[487px] [@media(min-width:940px)]:gap-2 [@media(min-width:940px)]:p-0 [@media(min-width:940px)]:py-6 lg:h-[303px] lg:items-start lg:justify-center lg:gap-0 lg:py-0">
            {blok.title && (
              <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05">
                {blok.title}
              </h2>
            )}
            {blok.subtitle && (
              <p className="max-w-[254px] text-h6 font-bold leading-normal text-[#4f4f4f] [@media(min-width:940px)]:max-w-none lg:p-2.5">
                {blok.subtitle}
              </p>
            )}
          </div>

          {id ? (
            <div className="relative aspect-video w-full [@media(min-width:940px)]:ml-auto [@media(min-width:940px)]:aspect-538/303 [@media(min-width:940px)]:w-[540px] [@media(min-width:940px)]:shrink-0 lg:w-[700px]">
              <YouTubePlayer videoId={id} title={blok.title} />
            </div>
          ) : (
            <div className="aspect-video w-full bg-black/5 [@media(min-width:940px)]:ml-auto [@media(min-width:940px)]:aspect-538/303 [@media(min-width:940px)]:w-[540px] [@media(min-width:940px)]:shrink-0 lg:w-[700px]" />
          )}
        </div>
      </div>
    </section>
  );
}
